import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsTextTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [formData, setFormData] = useState({
    name: '',
    category: 'Quotes',
    content: '',
    shortcode: ''
  });

  const categories = ['Quotes', 'Jobs', 'Invoices', 'PPM', 'General'];
  const filterTabs = ['All', ...categories];

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/text-templates');
      const data = await res.json();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingTemplate
        ? `/api/text-templates/${editingTemplate.id}`
        : '/api/text-templates';
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      fetchTemplates();
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ name: '', category: 'Quotes', content: '', shortcode: '' });
    } catch (err) {
      console.error('Error saving template:', err);
    }
  };

  const handleEdit = (template) => {
    if (template.is_locked) {
      alert('This template is locked and cannot be edited. Unlock it first.');
      return;
    }
    
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      content: template.content,
      shortcode: template.shortcode || ''
    });
    setShowModal(true);
  };

  const handleToggleLock = async (template) => {
    try {
      await apiFetch(`/api/text-templates/${template.id}/lock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_locked: !template.is_locked })
      });
      fetchTemplates();
    } catch (err) {
      console.error('Error toggling lock:', err);
      alert('Failed to toggle lock status');
    }
  };

  const handleDelete = async (id, isLocked) => {
    if (isLocked) {
      alert('This template is locked and cannot be deleted. Unlock it first.');
      return;
    }
    
    if (!window.confirm('Delete this template?')) return;
    
    try {
      const res = await apiFetch(`/api/text-templates/${id}`, {
        method: 'DELETE'
      });
      
      if (!res.ok) {
        const error = await res.json();
        alert(error.error || 'Failed to delete template');
        return;
      }
      
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template');
    }
  };

  const openNewModal = () => {
    setEditingTemplate(null);
    setFormData({ name: '', category: 'Quotes', content: '', shortcode: '' });
    setShowModal(true);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading templates...</div>;
  }

  // Filter templates by active category
  const filteredTemplates = activeCategory === 'All' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Note Templates</h2>
          <p className="text-sm text-blue-100 mt-1">Manage reusable text snippets for descriptions and notes</p>
        </div>
        <button
          onClick={openNewModal}
          className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
        >
          + New Template
        </button>
      </div>
      
      <div className="p-6 space-y-6">

      {/* Category Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        {filterTabs.map(tab => {
          const count = tab === 'All' 
            ? templates.length 
            : templates.filter(t => t.category === tab).length;
          
          return (
            <button
              key={tab}
              onClick={() => setActiveCategory(tab)}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition ${
                activeCategory === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              {tab}
              <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Templates Table */}
      <div className="bg-blue-50 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Shortcode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content Preview</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTemplates.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                  <p className="text-lg mb-2">
                    {templates.length === 0 ? 'No templates yet' : `No ${activeCategory} templates`}
                  </p>
                  <p className="text-sm">
                    {templates.length === 0 ? 'Create your first template to get started' : 'Try a different category'}
                  </p>
                </td>
              </tr>
            ) : (
              filteredTemplates.map(template => (
                <tr key={template.id} className={`hover:bg-gray-50 ${template.is_locked ? 'bg-amber-50' : ''}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    {template.is_locked && (
                      <span className="text-amber-500" title="Locked template">🔒</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {template.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {template.shortcode && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">
                        {template.shortcode}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-600 truncate max-w-md" title={template.content}>
                      {template.content}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggleLock(template)}
                        className={`px-2 py-1 rounded transition ${
                          template.is_locked 
                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={template.is_locked ? 'Unlock template' : 'Lock template'}
                      >
                        {template.is_locked ? '🔓' : '🔒'}
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        disabled={template.is_locked}
                        className={`px-2 py-1 rounded transition ${
                          template.is_locked
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={template.is_locked ? 'Unlock to edit' : 'Edit template'}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(template.id, template.is_locked)}
                        disabled={template.is_locked}
                        className={`px-2 py-1 rounded transition ${
                          template.is_locked
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                        }`}
                        title={template.is_locked ? 'Unlock to delete' : 'Delete template'}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Automatic Door Repair"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shortcode (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.shortcode}
                      onChange={(e) => setFormData({...formData, shortcode: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., auto-repair"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="The text that will be inserted when this template is selected..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This text will be inserted when you click this template in the picker
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg transition font-medium"
                  >
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default SettingsTextTemplates;
